// @ts-check
// ====== F11: Backup ZIP cifrato AES-256-GCM ======
// Usa Web Crypto API (built-in, no dep esterna):
//   - PBKDF2 (SHA-256, 250k iter) per derivare la chiave dalla passphrase utente
//   - AES-GCM 256-bit per cifrare il payload
//   - Output: blob binario con header magico + salt + iv + ciphertext
//
// Formato file (.mase.enc):
//   bytes 0–7   : magic "MASEENC1"
//   bytes 8–23  : salt (16 byte)
//   bytes 24–35 : iv (12 byte, GCM standard)
//   bytes 36+   : ciphertext (include il tag GCM)
//
// Compatibile con import altrettanto fatto qui (decryptBackupFile).
// NB: niente ZIP container (la struttura tabulare è già un singolo JSON,
// quindi un secondo livello di archive sarebbe overhead inutile). Il file
// .mase.enc è completo standalone.

const MAGIC = new TextEncoder().encode('MASEENC1');
const PBKDF2_ITER = 250000;

/** Concatena Uint8Array. */
function concat(...arrs) {
  let total = 0;
  arrs.forEach((a) => (total += a.length));
  const out = new Uint8Array(total);
  let off = 0;
  arrs.forEach((a) => {
    out.set(a, off);
    off += a.length;
  });
  return out;
}

async function deriveKey(passphrase, salt) {
  const enc = new TextEncoder();
  const km = await crypto.subtle.importKey('raw', enc.encode(passphrase), 'PBKDF2', false, [
    'deriveKey'
  ]);
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      hash: 'SHA-256',
      salt,
      iterations: PBKDF2_ITER
    },
    km,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Cifra un payload JSON-serializzabile e restituisce un Blob .mase.enc
 * @param {unknown} payload
 * @param {string} passphrase
 */
export async function encryptBackup(payload, passphrase) {
  if (!passphrase || passphrase.length < 8)
    throw new Error('La passphrase deve essere di almeno 8 caratteri');
  const json = JSON.stringify(payload);
  const data = new TextEncoder().encode(json);
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveKey(passphrase, salt);
  const cipher = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, data));
  const out = concat(MAGIC, salt, iv, cipher);
  return new Blob([out], { type: 'application/octet-stream' });
}

/**
 * Decifra un file .mase.enc → JSON object.
 * @param {Blob | File | ArrayBuffer} blob
 * @param {string} passphrase
 */
export async function decryptBackup(blob, passphrase) {
  const buf =
    blob instanceof ArrayBuffer ? new Uint8Array(blob) : new Uint8Array(await blob.arrayBuffer());
  if (buf.length < MAGIC.length + 16 + 12 + 16) throw new Error('File troppo corto / malformato');
  for (let i = 0; i < MAGIC.length; i++)
    if (buf[i] !== MAGIC[i]) throw new Error('Magic header non riconosciuto (non è un .mase.enc)');
  const salt = buf.slice(8, 24);
  const iv = buf.slice(24, 36);
  const ct = buf.slice(36);
  const key = await deriveKey(passphrase, salt);
  let plain;
  try {
    plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ct);
  } catch (_) {
    throw new Error('Decryption fallita (passphrase errata o file corrotto)');
  }
  return JSON.parse(new TextDecoder().decode(plain));
}

/**
 * Helper: scarica il blob come file con nome dato.
 */
export function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

// @ts-ignore — esposizione globale temporanea
globalThis.maseCrypto = { encryptBackup, decryptBackup, saveBlob };
